import config from '../config';
import { getAccessToken } from './authStorage';
import { normalizeClientVersion } from '../utils/appVersion';

const APP_VERSION = normalizeClientVersion(process.env.REACT_APP_VERSION);
const WEB_PUSH_SUBSCRIPTION_UUID_KEY = `${config.tokenKey}_webpush_subscription_uuid`;

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const getStoredSubscriptionUuid = () => getStorage()?.getItem(WEB_PUSH_SUBSCRIPTION_UUID_KEY) || '';

const setStoredSubscriptionUuid = (subscriptionUuid) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  if (subscriptionUuid) {
    storage.setItem(WEB_PUSH_SUBSCRIPTION_UUID_KEY, subscriptionUuid);
    return;
  }

  storage.removeItem(WEB_PUSH_SUBSCRIPTION_UUID_KEY);
};

const buildApiUrl = (path) => {
  const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl.slice(0, -1) : config.apiBaseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

const createPushError = (message, code, status) => {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  return error;
};

const parseResponseBody = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }

  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const request = async (path, options = {}) => {
  const token = getAccessToken();
  const headers = {
    'X-Client-Version': APP_VERSION,
    'X-Client-Platform': 'web',
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildApiUrl(path), {
    credentials: 'include',
    ...options,
    headers,
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    const structured = body?.error && typeof body.error === 'object' ? body.error : null;
    const message = structured?.message || body?.message || `Push request failed with status ${response.status}.`;
    const code = structured?.code || body?.code || 'PUSH_REQUEST_FAILED';
    throw createPushError(message, code, response.status);
  }

  return body;
};

const extractPayload = (body) => body?.data || body || {};

const isServiceWorkerSupported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

const isPushSupported = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return isServiceWorkerSupported() && 'PushManager' in window && 'Notification' in window;
};

const isSecurePushContext = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const decodeBase64Url = (value) => {
  const normalized = value
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const decoded = window.atob(padded);
  return Uint8Array.from(decoded, (char) => char.charCodeAt(0));
};

const extractVapidPublicKey = (body) => {
  const payload = extractPayload(body);

  if (typeof payload === 'string') {
    return payload;
  }

  return payload.publicKey || payload.vapidPublicKey || payload.key || '';
};

const resolveVapidPublicKey = async () => {
  if (config.push.vapidPublicKey) {
    return config.push.vapidPublicKey;
  }

  const body = await request('/push/vapid-public-key');
  const vapidPublicKey = extractVapidPublicKey(body);
  if (!vapidPublicKey) {
    throw createPushError('Push VAPID key is not configured for this environment.', 'PUSH_VAPID_KEY_MISSING', 500);
  }

  return vapidPublicKey;
};

export const registerPushServiceWorker = async () => {
  if (!isPushSupported()) {
    return null;
  }

  return navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' });
};

const getExistingSubscription = async () => {
  if (!isPushSupported()) {
    return null;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    return null;
  }

  return registration.pushManager.getSubscription();
};

export const getWebPushStatus = async () => {
  if (!isPushSupported()) {
    return {
      supported: false,
      secureContext: isSecurePushContext(),
      permission: 'unsupported',
      enabled: false,
    };
  }

  const subscription = await getExistingSubscription();

  return {
    supported: true,
    secureContext: isSecurePushContext(),
    permission: Notification.permission,
    enabled: Boolean(subscription),
  };
};

export const enableWebPush = async () => {
  if (!isPushSupported()) {
    throw createPushError('This browser does not support service workers or Web Push.', 'PUSH_NOT_SUPPORTED');
  }

  if (!isSecurePushContext()) {
    throw createPushError('Browser alerts require HTTPS or localhost before Web Push can be enabled.', 'PUSH_INSECURE_CONTEXT');
  }

  const registration = await registerPushServiceWorker();
  if (!registration) {
    throw createPushError('Service worker registration is unavailable in this browser.', 'PUSH_SW_UNAVAILABLE');
  }

  if (Notification.permission === 'denied') {
    throw createPushError('Browser alerts are blocked. Enable notifications in your browser settings and reload.', 'PUSH_PERMISSION_DENIED');
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }

  if (permission !== 'granted') {
    throw createPushError('Browser alerts were not granted. Turn them on in your browser settings to continue.', 'PUSH_PERMISSION_NOT_GRANTED');
  }

  const vapidPublicKey = await resolveVapidPublicKey();
  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription = existingSubscription || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeBase64Url(vapidPublicKey),
  });
  const subscriptionJson = subscription.toJSON();

  const body = await request('/push/subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      channel: 'webpush',
      token: subscription.endpoint,
      web_p256dh: subscriptionJson.keys?.p256dh || null,
      web_auth: subscriptionJson.keys?.auth || null,
      platform: 'web',
      app_version: APP_VERSION,
      locale: typeof navigator !== 'undefined' ? navigator.language : 'en-GB',
    }),
  });
  const payload = extractPayload(body);
  setStoredSubscriptionUuid(payload.uuid || payload.id || '');

  return {
    permission,
    subscription,
    subscriptionUuid: payload.uuid || payload.id || '',
  };
};

export const disableWebPush = async () => {
  if (!isPushSupported()) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = registration ? await registration.pushManager.getSubscription() : null;
  const subscriptionUuid = getStoredSubscriptionUuid();

  if (subscriptionUuid) {
    try {
      await request(`/push/subscriptions/${subscriptionUuid}`, {
        method: 'DELETE',
      });
    } catch (error) {
      if (error.status !== 404) {
        throw error;
      }
    }
  }

  if (subscription) {
    await subscription.unsubscribe();
  }

  setStoredSubscriptionUuid('');
};
